// frontend/src/pages/NotFoundPage.tsx - UPDATED FOR HOOKS/STORE INTEGRATION
import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { PageLayout } from '@/components/layout/PageLayout';
import { Button } from '@/components/ui/Button';
import { useAuthStore } from '@/store/auth';
import { useAppStore } from '@/store/app';

export const NotFoundPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuthStore();
  const { clearGlobalError, clearAllApiErrors } = useAppStore();

  // Clear any existing errors when landing on 404 page
  useEffect(() => {
    clearGlobalError();
    clearAllApiErrors();
  }, [clearGlobalError, clearAllApiErrors]);

  // Role-based navigation suggestions
  const getNavigationSuggestions = () => {
    if (!isAuthenticated || !user) {
      return [
        { to: '/login', label: 'Login', description: 'Access your account' }
      ];
    }

    const suggestions = [
      { to: '/', label: 'Dashboard', description: 'Main dashboard' },
      { to: '/correspondences', label: 'Correspondences', description: 'View all correspondences' },
      { to: '/responses', label: 'Responses', description: 'Manage responses' },
      { to: '/projects', label: 'Projects', description: 'View projects' }
    ];

    // Add role-specific suggestions
    if (user.role === 'admin' || user.role === 'implementor') {
      suggestions.push(
        { to: '/users', label: 'Users', description: 'Manage users' },
        { to: '/workflows', label: 'Workflows', description: 'Manage workflows' }
      );
    }

    if (user.role === 'implementor') {
      suggestions.push(
        { to: '/organizations', label: 'Organizations', description: 'Manage organizations' },
        { to: '/admin', label: 'Admin Panel', description: 'System administration' }
      );
    }

    // Always include settings
    suggestions.push(
      { to: '/settings', label: 'Settings', description: 'Account settings' },
      { to: '/profile', label: 'Profile', description: 'Your profile' }
    );

    return suggestions;
  };

  const handleGoBack = () => {
    // Use navigate with fallback to dashboard
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate(isAuthenticated ? '/' : '/login');
    }
  };

  const handleGoToDashboard = () => {
    navigate(isAuthenticated ? '/' : '/login');
  };

  const navigationSuggestions = getNavigationSuggestions();

  return (
    <PageLayout 
      title="Page Not Found"
      className="min-h-screen"
    >
      <div className="min-h-full flex flex-col justify-center items-center px-4 py-16">
        <div className="text-center max-w-2xl">
          {/* 404 Illustration - Enhanced */}
          <div className="mb-8">
            <svg
              className="mx-auto h-48 w-48 sm:h-64 sm:w-64 text-gray-300 dark:text-gray-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={0.5}
                d="M9.172 16.172a4 4 0 015.656 0M9 12h6m-6-4h6m2 5.291A7.962 7.962 0 0112 15c-2.34 0-4.29-1.044-5.709-2.573M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>

          {/* Error Code */}
          <h1 className="text-6xl sm:text-9xl font-bold text-gray-400 dark:text-gray-500 mb-4">
            404
          </h1>

          {/* Error Message */}
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100 mb-4">
            Page Not Found
          </h2>
          
          <p className="text-base sm:text-lg text-gray-600 dark:text-gray-400 mb-8 max-w-md mx-auto">
            Sorry, we couldn't find the page you're looking for. The page might have been moved, deleted, or you entered the wrong URL.
          </p>

          {/* User Context Message */}
          {isAuthenticated && user && (
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
              Hi {user.firstName || user.username}, here are some pages you might be looking for:
            </p>
          )}

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-12">
            <Button
              variant="primary"
              size="lg"
              onClick={handleGoToDashboard}
              className="min-w-[160px]"
            >
              {isAuthenticated ? 'Go to Dashboard' : 'Go to Login'}
            </Button>
            
            <Button
              variant="secondary"
              size="lg"
              onClick={handleGoBack}
              className="min-w-[160px]"
            >
              Go Back
            </Button>
          </div>

          {/* Navigation Suggestions - Role-based */}
          <div className="pt-8 border-t border-gray-200 dark:border-gray-700">
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
              Try these pages instead:
            </p>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-4xl mx-auto">
              {navigationSuggestions.slice(0, 6).map((suggestion) => (
                <Link
                  key={suggestion.to}
                  to={suggestion.to}
                  className="block p-4 bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 hover:shadow-md hover:border-blue-300 dark:hover:border-blue-600 transition-all duration-200 text-left group"
                >
                  <div className="font-medium text-gray-900 dark:text-gray-100 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                    {suggestion.label}
                  </div>
                  {suggestion.description && (
                    <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                      {suggestion.description}
                    </div>
                  )}
                </Link>
              ))}
            </div>
          </div>

          {/* Additional Help */}
          <div className="mt-12 pt-8 border-t border-gray-200 dark:border-gray-700">
            <div className="space-y-4">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Still can't find what you're looking for?
              </p>
              
              {isAuthenticated && (
                <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                  <Link
                    to="/settings"
                    className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 hover:underline text-sm"
                  >
                    Check Settings
                  </Link>
                  <span className="hidden sm:inline text-gray-300 dark:text-gray-600">•</span>
                  <Link
                    to="/profile"
                    className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 hover:underline text-sm"
                  >
                    View Profile
                  </Link>
                  {(user?.role === 'admin' || user?.role === 'implementor') && (
                    <>
                      <span className="hidden sm:inline text-gray-300 dark:text-gray-600">•</span>
                      <Link
                        to="/admin"
                        className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 hover:underline text-sm"
                      >
                        Admin Panel
                      </Link>
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Contact Support */}
            <div className="mt-8">
              <p className="text-xs text-gray-400 dark:text-gray-500">
                If you believe this is an error, please contact support.
              </p>
            </div>
          </div>
        </div>
      </div>
    </PageLayout>
  );
};